import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PrintIcon from '@mui/icons-material/Print';
import BlockIcon from '@mui/icons-material/Block';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import devolucionVentaService, { DevolucionVentaItem } from '../../services/devolucionVentaService';
import PDFViewer from '../shared/PDFViewer';
import { getFileNameFromContentDisposition } from '../../utils/contentDisposition';
import { formatComprobanteTesoreria, formatCurrency } from '../../utils/formatters';
import api from '../../services/api';
import FiltroSucursalVentaSelect from '../shared/FiltroSucursalVentaSelect';
import { resolveNroSucFiltro, useSucursalesVentaFiltro } from '../../hooks/useSucursalesVentaFiltro';
import {
  loadTesoreriaFiltros,
  storedNumber,
  storedString,
  TESORERIA_FILTROS_KEYS,
  usePersistTesoreriaFiltros,
} from '../../utils/tesoreriaFiltrosStorage';

const ESTADO_DEVOLUCION_OPTIONS = ['activos', 'anulados', 'todos'] as const;
type FiltroEstadoDevolucion = (typeof ESTADO_DEVOLUCION_OPTIONS)[number];

interface DevolucionesFiltrosStored {
  searchTerm?: string;
  fechaDesde?: string;
  fechaHasta?: string;
  filtroEstado?: FiltroEstadoDevolucion;
  filtroNroSuc?: number | null;
  rowsPerPage?: number;
}

const formatComprobante = (row: DevolucionVentaItem): string =>
  formatComprobanteTesoreria(row.letra || 'X', row.nroSuc, row.nroComprobante);

const Devoluciones: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const getFirstDayOfMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
  };

  const getLastDayOfMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
  };

  const filtrosGuardados = () => {
    const parsed = loadTesoreriaFiltros<DevolucionesFiltrosStored>(TESORERIA_FILTROS_KEYS.devoluciones);
    if (parsed.filtroEstado && !ESTADO_DEVOLUCION_OPTIONS.includes(parsed.filtroEstado)) {
      parsed.filtroEstado = 'activos';
    }
    return parsed;
  };

  const [items, setItems] = useState<DevolucionVentaItem[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(() => storedNumber(filtrosGuardados(), 'rowsPerPage', 10));
  const [searchTerm, setSearchTerm] = useState(() => storedString(filtrosGuardados(), 'searchTerm', ''));
  const [fechaDesde, setFechaDesde] = useState(() =>
    storedString(filtrosGuardados(), 'fechaDesde', getFirstDayOfMonth())
  );
  const [fechaHasta, setFechaHasta] = useState(() =>
    storedString(filtrosGuardados(), 'fechaHasta', getLastDayOfMonth())
  );
  const [filtroEstado, setFiltroEstado] = useState<FiltroEstadoDevolucion>(
    () => filtrosGuardados().filtroEstado ?? 'activos'
  );
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [selectedDevolucion, setSelectedDevolucion] = useState<number | null>(null);
  const [pdfFileName, setPdfFileName] = useState<string | null>(null);
  const [anulandoId, setAnulandoId] = useState<number | null>(null);
  const { filtroNroSuc, setFiltroNroSuc, sucursales, loadingSucursales, filtroListo } = useSucursalesVentaFiltro({
    resetPreferidaOnNavigation: true,
  });

  const esDevolucionAnulada = (row: DevolucionVentaItem) =>
    (row.asentado ?? '').trim().toUpperCase() === 'A' || (row.nombre ?? '').trim().toUpperCase() === 'ANULADO';

  const puedeMostrarAnular = (row: DevolucionVentaItem) => !esDevolucionAnulada(row);

  const formatMotivosAnulacion = (motivos?: string[] | null, fallback = 'No se puede anular esta devolución') => {
    const list = (motivos ?? []).map((m) => String(m).trim()).filter(Boolean);
    return list.length > 0 ? list.join('\n') : fallback;
  };

  const handleAnular = async (row: DevolucionVentaItem) => {
    if (!puedeMostrarAnular(row)) return;

    try {
      const check = await devolucionVentaService.puedeAnular(row.nroFormulario);
      if (!check.puedeAnular) {
        enqueueSnackbar(formatMotivosAnulacion(check.motivos), {
          variant: 'warning',
          style: { whiteSpace: 'pre-line' },
          autoHideDuration: 8000,
        });
        return;
      }
    } catch (error: any) {
      const msg = formatMotivosAnulacion(
        error?.response?.data?.motivos,
        error?.response?.data?.message ?? 'No se pudo validar la anulación'
      );
      enqueueSnackbar(msg, {
        variant: 'error',
        style: { whiteSpace: 'pre-line' },
        autoHideDuration: 8000,
      });
      return;
    }

    if (
      !window.confirm(
        '¿Está seguro que desea anular esta devolución? Se revertirán las aplicaciones asociadas y el reintegro de efectivo en caja.'
      )
    ) {
      return;
    }

    setAnulandoId(row.nroFormulario);
    try {
      await devolucionVentaService.anular(row.nroFormulario);
      enqueueSnackbar('Devolución anulada correctamente', { variant: 'success' });
      await fetchDevoluciones();
    } catch (error: any) {
      const data = error?.response?.data;
      const message = formatMotivosAnulacion(
        data?.motivos,
        data?.message ??
          (Array.isArray(data?.errors?.general) ? data.errors.general[0] : null) ??
          'Error al anular la devolución'
      );
      enqueueSnackbar(message, {
        variant: 'error',
        style: { whiteSpace: 'pre-line' },
        autoHideDuration: 8000,
      });
    } finally {
      setAnulandoId(null);
    }
  };

  const handleImprimirPDF = async (nroFormulario: number) => {
    try {
      const response = await api.get(`/api/DevolucionesVenta/${nroFormulario}/pdf`, { responseType: 'blob' });
      const cd = (response.headers as Record<string, string | undefined>)?.['content-disposition'];
      setPdfFileName(getFileNameFromContentDisposition(cd));
      const url = window.URL.createObjectURL(new Blob([response.data]));
      setPdfUrl(url);
      setSelectedDevolucion(nroFormulario);
    } catch {
      enqueueSnackbar('Error al generar el PDF de la devolución', { variant: 'error' });
    }
  };

  const handleClosePDF = () => {
    if (pdfUrl) window.URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
    setSelectedDevolucion(null);
    setPdfFileName(null);
  };

  const fetchDevoluciones = async () => {
    try {
      const nroSucValue = resolveNroSucFiltro(filtroNroSuc);
      // /api/DevolucionesVenta no aplica NroSuc en el listado (a diferencia de recibos).
      // Con sucursal elegida traemos el período y filtramos/paginamos en cliente por item.nroSuc.
      const pageSizeFetch = nroSucValue != null ? 200 : rowsPerPage;
      const baseParams: Record<string, string | number> = {
        pageNumber: nroSucValue != null ? 1 : page + 1,
        pageSize: pageSizeFetch,
      };
      if (searchTerm.trim()) baseParams.searchTerm = searchTerm.trim();
      if (fechaDesde) baseParams.fechaDesde = fechaDesde;
      if (fechaHasta) baseParams.fechaHasta = fechaHasta;
      if (filtroEstado === 'anulados') baseParams.asentado = 'A';
      else if (filtroEstado === 'activos') baseParams.asentado = 'activos';

      if (nroSucValue == null) {
        const response = await devolucionVentaService.list(baseParams);
        setItems(response.items);
        setTotalRecords(response.totalCount);
        return;
      }

      const first = await devolucionVentaService.list(baseParams);
      let allItems = [...first.items];
      const totalPages = Math.max(1, Number(first.totalPages) || 1);
      const maxPages = Math.min(totalPages, 25);
      for (let p = 2; p <= maxPages; p += 1) {
        const more = await devolucionVentaService.list({ ...baseParams, pageNumber: p });
        allItems = allItems.concat(more.items);
      }

      const filtered = allItems.filter((row) => Number(row.nroSuc) === nroSucValue);
      const start = page * rowsPerPage;
      setItems(filtered.slice(start, start + rowsPerPage));
      setTotalRecords(filtered.length);
    } catch (error: any) {
      const msg = error?.response?.data?.mensaje || error?.response?.data?.message || 'Error al cargar devoluciones';
      enqueueSnackbar(msg, { variant: 'error' });
    }
  };

  usePersistTesoreriaFiltros(
    TESORERIA_FILTROS_KEYS.devoluciones,
    { searchTerm, fechaDesde, fechaHasta, filtroEstado, rowsPerPage },
    [searchTerm, fechaDesde, fechaHasta, filtroEstado, rowsPerPage]
  );

  useEffect(() => {
    if (!filtroListo) return;
    fetchDevoluciones();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, searchTerm, fechaDesde, fechaHasta, filtroEstado, filtroNroSuc, filtroListo]);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/tesoreria/recibos')} size="small" sx={{ textTransform: 'none' }}>
                  Recibos
                </Button>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                  Devoluciones
                </Typography>
              </Stack>
              <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => navigate('/tesoreria/devoluciones/nuevo')}>
                Nueva devolución
              </Button>
            </Stack>

            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  placeholder="Buscar por número, comprobante o cliente..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField fullWidth type="date" label="Desde" InputLabelProps={{ shrink: true }} value={fechaDesde} onChange={(e) => { setFechaDesde(e.target.value); setPage(0); }} />
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField fullWidth type="date" label="Hasta" InputLabelProps={{ shrink: true }} value={fechaHasta} onChange={(e) => { setFechaHasta(e.target.value); setPage(0); }} />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Estado</InputLabel>
                  <Select value={filtroEstado} label="Estado" onChange={(e) => { setFiltroEstado(e.target.value as typeof filtroEstado); setPage(0); }}>
                    <MenuItem value="activos">Activos</MenuItem>
                    <MenuItem value="anulados">Anulados</MenuItem>
                    <MenuItem value="todos">Todos</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FiltroSucursalVentaSelect
                  value={filtroNroSuc}
                  onChange={(v) => { setFiltroNroSuc(v); setPage(0); }}
                  loading={loadingSucursales}
                  sucursales={sucursales}
                />
              </Grid>
            </Grid>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Comprobante</TableCell>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell align="right">Importe</TableCell>
                    <TableCell>Usuario</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                          No hay devoluciones para los filtros seleccionados.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    items.map((row) => {
                      const anulado = esDevolucionAnulada(row);
                      const anulando = anulandoId === row.nroFormulario;
                      return (
                        <TableRow key={row.nroFormulario} hover>
                          <TableCell>{formatComprobante(row)}</TableCell>
                          <TableCell>{row.fechaEmision ? new Date(row.fechaEmision).toLocaleDateString('es-AR') : '-'}</TableCell>
                          <TableCell>{row.nroCliente} - {row.nombre}</TableCell>
                          <TableCell align="right">{formatCurrency(row.importeTotal ?? 0)}</TableCell>
                          <TableCell>{row.usuario || '-'}</TableCell>
                          <TableCell>
                            <Chip size="small" label={anulado ? 'Anulado' : 'Activo'} color={anulado ? 'default' : 'success'} variant="outlined" />
                          </TableCell>
                          <TableCell align="center">
                            <Stack direction="row" spacing={0.5} justifyContent="center">
                              <Tooltip title="Imprimir PDF">
                                <IconButton
                                  onClick={() => handleImprimirPDF(row.nroFormulario)}
                                  size="small"
                                  sx={{
                                    bgcolor: '#2196f3',
                                    color: '#fff',
                                    borderRadius: 1.5,
                                    width: 32,
                                    height: 32,
                                    '&:hover': { bgcolor: '#1976d2', transform: 'scale(1.1)' },
                                    transition: 'all 0.2s',
                                  }}
                                >
                                  <PrintIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              {puedeMostrarAnular(row) && (
                                <Tooltip title="Anular devolución">
                                  <IconButton
                                    onClick={() => void handleAnular(row)}
                                    disabled={anulando}
                                    size="small"
                                    sx={{
                                      bgcolor: '#f44336',
                                      color: '#fff',
                                      borderRadius: 1.5,
                                      width: 32,
                                      height: 32,
                                      '&:hover': { bgcolor: '#d32f2f', transform: 'scale(1.1)' },
                                      '&:disabled': { bgcolor: '#ef9a9a', color: '#fff' },
                                      transition: 'all 0.2s',
                                    }}
                                  >
                                    {anulando ? (
                                      <CircularProgress size={16} sx={{ color: '#fff' }} />
                                    ) : (
                                      <BlockIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={totalRecords}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[5, 10, 20, 50]}
              labelRowsPerPage="Filas por página"
            />
          </Stack>
        </CardContent>
      </Card>

      {pdfUrl && selectedDevolucion && (
        <PDFViewer
          pdfUrl={pdfUrl}
          onClose={handleClosePDF}
          title={`Devolución ${selectedDevolucion}`}
          documentId={`${selectedDevolucion}`}
          documentType="devolucion"
          downloadFileName={pdfFileName ?? undefined}
        />
      )}
    </Box>
  );
};

export default Devoluciones;
